<template>
  <v-flex
    xs8
    offset-xs2
    align-self-center
    justify-center
  >
  <div>
    <v-container fill-height fluid>
    <v-row>
      <v-flex xs3 class="mr-2">
        <Search
          v-model="filters.search"
          :label="$t('SEARCH')"
          prepend-icon="search"
          :delay="500"
        />
      </v-flex>
      <v-spacer></v-spacer>
      <v-btn
        v-if="can('create subjects')"
        @click="showSubjectModal(0)"
        color="primary"
        dark
        class="mb-2 mr-2"
      >{{ $t("NEW") }}
      </v-btn>
      <v-btn
        v-if="can('create subjects')"
        @click="exportData()"
        dark
        class="mb-2"
      >{{ $t("EXPORT") }}
      </v-btn>
    </v-row>
    </v-container>
    <v-data-table
      :headers="headers"
      :items="items"
      :server-items-length="totalItems"
      :no-data-text="$t('NO_DATA')"
      :no-results-text="$t('NO_DATA')"
      hide-default-footer
      :options.sync="pagination"
      :loading="loading"
      class="elevation-1"
    >
      <template v-slot:item.name="{ item }">
        <router-link :to="{ name: routeName.SUBJECT_SHOW, params: { id: item.id } }">
            {{ item.name }}
        </router-link>
      </template>
      <template v-slot:item.printOrder="{ item }">
            <span v-if="item.options">{{ item.options.printOrder}}</span>
      </template>

      <template v-slot:item.is_primary="{ item }">
            <v-icon v-if="(item.options) && item.options.is_primary">
              check
            </v-icon>
            <v-icon v-else>close</v-icon>

      </template>

      <template v-slot:item.is_secondary="{ item }">
            <v-icon v-if="(item.options) && item.options.is_secondary">
              check
            </v-icon>
            <v-icon v-else>close</v-icon>
      </template>

      <template v-slot:item.actions="{ item }">
            <v-layout wrap>
              <v-flex class="text-no-wrap">
                <v-btn
                  icon
                  color="blue"
                  v-if="can('edit subjects')"
                  @click="showSubjectModal(item.id)"
                >
                  <v-icon>edit</v-icon>
                </v-btn>
                <v-btn
                  v-if="can('delete subjects')"
                  icon
                  color="red"
                  @click="deleteSubject(item.id)"
                >
                  <v-icon>cancel</v-icon>
                </v-btn>
              </v-flex>
            </v-layout>
      </template>
    </v-data-table>
    <div class="pt-2">{{$t('fields.total_items')}} {{ totalItems }}</div>
    <Paginator
      :total-items="totalItems"
      :page.sync="pagination.page"
      :items-per-page.sync="pagination.itemsPerPage"
      @change="fetchSubjects"
    />
  </div>
  </v-flex>
</template>
<script>
  import { mapActions } from 'vuex';

  import { subjectsService } from '../../utils/services/subjects.service';
  import Search from '../../components/shared/Inputs/Search';
  import Loader from '../../components/shared/Loader';
  import SubjectModal from '../../components/modals/Subjects/SubjectModal';
  import Paginator from '../../components/shared/Paginator';

  export default {
    name: 'SubjectList',
    components: { Search, Loader, Paginator },
    data() {
      return {
        loading: false,
        items: [],
        totalItems: 0,
        pagination: {
          page: 1,
          itemsPerPage: 10,
          sortDesc: [false],
          sortBy: ['name'],
        },
        headers: [
          {
            text: this.$t('fields.id'),
            align: 'left',
            value: 'id',
          },
          { text: this.$t('fields.name'), value: 'name' },
          { text: this.$t('fields.primary_school'), value: 'is_primary', sortable: true },
          { text: this.$t('fields.secondary_school'), value: 'is_secondary', sortable: true },
          { text: this.$t('fields.print_order'), value: 'printOrder', sortable: true },
          { text: this.$t('fields.actions'), value: 'actions', sortable: false, align: 'center' },
        ],
        filters: {
          search: null,
        },
      };
    },
    computed: {
      requestFilters() {
        const filters = {
          search: this.filters.search,
        };
        if (this.pagination.sortBy.length === 1 && this.pagination.sortDesc.length === 1) {
          const { sortBy, sortDesc } = this.pagination;
          filters.sortBy = sortBy;
          filters.sortDesc = sortDesc;
        }
        return filters;
      },
    },
    methods: {
      ...mapActions('confirm', ['showConfirm']),
      ...mapActions('modal', ['showModal', 'hideModal']),
      ...mapActions('alert', ['showToast']),
      async fetchSubjects(forcePage) {
        this.loading = true;
        const { page, itemsPerPage } = this.pagination;
        return subjectsService.index({
          page: page || forcePage || 1,
          itemsPerPage,
          filters: this.requestFilters,
        })
          .then(response => {
            this.items = response.data.data;
            this.totalItems = response.data.meta.total;
            this.pagination = this.getPagination(
              response.data.meta,
              { oldPagination: this.pagination, forcePage },
            );
          })
          .catch(error => {
            this.handleError(error, this.errors);
          })
          .finally(() => {
            this.loading = false;
          });
      },
      exportData() {
        this.loading = true;
        return subjectsService.exportData({
          filters: this.requestFilters,
        })
          .then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'subjects.csv');
            document.body.appendChild(link);
            link.click();
          })
          .catch(error => {
            this.handleError(error, this.errors);
          })
          .finally(() => {
            this.loading = false;
          });
      },
      async showSubjectModal(id) {
        this.showModal({
          component: SubjectModal,
            data: {
              stored: {
                id,
              },
          },
          options: {
            title: this.$t('SUBJECT'),
            onSave: (
              form,
              error,
            ) => subjectsService.save(form)
            .then(() => {
              this.hideModal({});
              this.showToast({ type: 'success', message: this.$t('SAVED') });
            })
            .catch(error => {
              this.handleError(error, this.errors);
            })
            .finally(() => {
              this.fetchSubjects(this.pagination.page);
            }),
          },
        });
      },
      async deleteSubject(id) {
          this.showConfirm({
            onConfirm: () => {
              this.loading = true;
              return subjectsService.remove(id)
              .then(() => {
                this.showToast({ type: 'success', message: this.$t('DELETED') });
                this.fetchSubjects(this.pagination.page);
              })
              .catch(error => {
                this.handleError(error, this.errors);
              })
              .finally(() => {
                this.loading = false;
              });
            },
          });
      },
    },
    async created() {
      await this.fetchSubjects(this.pagination.page);
    },
    watch: {
      filters: {
        deep: true,
        handler() {
          this.fetchSubjects(1);
        },
      },
      'pagination.sortBy': {
        handler(oldVal, newVal) {
          if (newVal !== oldVal) {
            this.fetchSubjects(1);
          }
        },
      },
    },
  };
</script>
